import dotProp from 'dot-prop-immutable'

import {
	merge,
	remove,
	isArray,
	mergeWith,
	union,
	forIn,
	find
} from 'lodash'
import copy from 'fast-copy'

import { normalize } from 'normalizr'
import * as schemas from 'state/schemas/schemas'
import { reportSilentPath, reportFillPath } from 'state/debug/task-watchdog'
import moment from 'moment'

import {
	getMobileOperatingSystem
} from 'utils'

import {
	UNAUTH_USER,
	UPDATE_FOLDER_CHAIN,
	GOT_ACCOUNT_DATA,
	CLEARED_ACCOUNT_DATA,
	PROJECT_GOT_DATA,
	PROJECT_SET_SLUG,
	PROJECT_STATUSES_SAVED,
	PROJECT_UPDATED,
	COMMENT_LIKED,
	TASK_SET_CURRENT,
	TASK_LOADING_DATA,
	FOLDER_ORDER_UPDATED,
	INVITES_POSTED,
	FILE_VERSION_DELETED,
	SUBSCRIBER_TOGGLED,
	TYPING_SET,
	CHAT_ADDED_USER,
	CHAT_RENAMED,
	NOTIFICATIONS_SEEN,
	NOTIFICATION_OPENED,
	NOTIFICATIONS_ALL_OPENED,
	NOTIFICATIONS_CLEARED,
} from '../actions/types'

const INITIAL_STATE = {
	currentAccount: null,
	currentAccountId: null,
	activeHeaderTab: null,
	folderChain: null,
	openFolderIndexes: [],
	currentFile: null,
	onlineUsers: [],
	currentProject: {
		loading: false
	},
	currentTask: {
		taskId: null,
		loading: false,
		comments: []
	},
	accountData: null,
	entities: {
		versions: {},
		files: {},
		tasks: {},
		milestones: {}
	},
	currentProjectId: null,
	currentProjectSlug: null,
	currentTaskId: null,
	typingUserData: {},
	hasHardSetState: false,
	optimisticTodo: null,
	optimisticChat: null,
	keyboardHeight: 0,
	chatBehaviourTrigger: 0, // if online users, focus, or typing are set, most components shouldn't do anything
	unseenNotifications: 0,
}

export default function (state = INITIAL_STATE, action) {
	if (!state.entities.versions) {
		state.entities.versions = {}
		state.entities.files = {}
		state.entities.sessions = {}
		state.entities.activities = {}
	}

	let currentEntityState

	let newUserData

	let normalizedResponseData
	let schema

	let chat = null

	// possible duplicates
	let tempNewState
	let newState = {}

	let applicableNotifications = {}
	let mergedNotifications = {}

	let typingUserData

	switch (action.type) {
		case 'FOCUS_SET':
		case 'typing_set':
		case 'online_users_changed':
		case 'chat_toggled':
			state.chatBehaviourTrigger = Math.random() // check this if any rogue updates are coming from chat to cut down render cycles
			break
	}

	switch (action.type) {
		case UNAUTH_USER:
			return { ...INITIAL_STATE }
		case 'ENTITY_ADDED_OR_UPDATED':
			if (typeof action.payload === 'string') {
				return state
			}

			if (action.entityType === 'note' && (action.payload.updated_at !== action.payload.created_at)) {
				// if the note has an updatedAt but no createdAt and we don't have it, we'll deleted it.
				if (!state.entities.notes[action.payload.id]) {
					return state
				}
			}

			let hasHardSetState = state.hasHardSetState

			if (action.isArray) {
				normalizedResponseData = normalize(action.payload, [schemas[action.entityType + 'Schema']])
			} else {
				normalizedResponseData = normalize(action.payload, schemas[action.entityType + 'Schema'])
			}

			currentEntityState = {
				...state.entities
			}

			// We shouldn't NEED to mutate state, but some parts gotta go before we merge them in.
			const mutatedKeys = []

			if (!hasHardSetState && action.hardSetAll) {
				currentEntityState.invitations = {}

				mutatedKeys.push('invitations')
			}

			if (action.entityType === 'dashboardData') {
				currentEntityState.dashboardData = null
				mutatedKeys.push('dashboardData')
			}

			// removing users over sockets - if they have an account, it'll overwrite the account users too somehow
			if (action.entityType === 'activity' && action.overwriteUsers) {
				delete normalizedResponseData.entities.accounts
			}

			if (action.hardSetAll) {
				hasHardSetState = true

				// Remove other accounts, as it'll clear their users.
				if (action.entityType === 'account' && !action.isArray) {
					const accountId = action.payload.id

					if (normalizedResponseData.entities.accounts && Object.keys(normalizedResponseData.entities.accounts).length > 1) {
						normalizedResponseData.entities.accounts = {
							[accountId]: normalizedResponseData.entities.accounts[accountId]
						}
					}
				}

				mergeWith(newState, currentEntityState, normalizedResponseData.entities, overwriteLogic)
			} else if (action.overwriteTasks) {
				mergeWith(newState, currentEntityState, normalizedResponseData.entities, overwriteTasksLogic)
			} else if (action.overwriteUsers) {
				mergeWith(newState, currentEntityState, normalizedResponseData.entities, overwriteUsersLogic)
			} else if (action.entityType === 'project') {
				mergeWith(newState, currentEntityState, normalizedResponseData.entities, overwriteGuestsAndInvitesLogic)
			} else {
				mergeWith(newState, currentEntityState, normalizedResponseData.entities, mergeLogic)
			}

			// each modified 'key' will realise it's been modified, ideally.
			mutatedKeys.forEach(key => {
				if (newState[key]) {
					newState[key] = {
						...newState[key]
					}
				}
			})

			const returnResponse = {
				...state,
				hasHardSetState,
				entities: {
					...state.entities
				}
			}

			forIn(normalizedResponseData.entities, (_, type) => {
				returnResponse.entities[type] = newState[type]
			})

			return returnResponse
		case 'ENTITY_DELETED':
			if (!action.payload) {
				break
			}

			newState = null
			currentEntityState = state.entities

			let parentType = 'account'

			switch (action.entityType) {
				case 'task':
					parentType = 'milestone'
					break
				case 'project':
					parentType = 'folder'
					break
				case 'note':
				case 'file':
				case 'milestone':
				case 'file_folder':
				case 'meeting':
					parentType = 'project'
					break
				case 'version':
					parentType = 'file'
					break
				case 'folder':
					parentType = 'folder'
					break
				case 'todo':
					parentType = 'todo_day'
					break
				case 'comment':
					parentType = 'task'
					break
				case 'chat':
				case 'account':
				case 'session':
					parentType = 'user'
					break
			}

			let parentKey

			if (parentType === 'todo_day') {
				parentKey = action.payload['tododay_id']
			} else if (action.payload.pivot && (action.payload.pivot.user_id || action.payload.pivot.account_id) && (parentType === 'account' || parentType === 'user')) {
				parentKey = action.payload.pivot[parentType + '_id']
			} else {
				if (action.payload[parentType + '_id']) {
					parentKey = action.payload[parentType + '_id']
				} else {
					parentKey = action.payload[parentType]
				}
			}

			let parent = state.entities[parentType + 's'][parentKey]

			const entityWithDeletedAt = {
				...action.payload,
				deleted_at: moment().format("YYYY-MM-DD HH:mm:ss")
			}

			currentEntityState = dotProp.set(currentEntityState, `${action.entityType}s.${action.payload.id}`, entityWithDeletedAt)

			if (!parent) {
				// just set deleted_at and get outta here
				return dotProp.set(state, `entities`, currentEntityState)
			}

			parent = copy(parent)

			// Folders skip this part. 
			if (!action.stateOnly && currentEntityState[action.entityType + 's']) {
				currentEntityState = dotProp.delete(currentEntityState, `${action.entityType}s.${action.payload.id}`)
			}

			// if task (or milestone), it might be archivedTasks
			let groupKey = action.entityType + 's'

			if (action.payload.deleted_at) {
				if (action.entityType === 'task') {
					groupKey = 'archivedTasks'
				} else if (action.entityType === 'milestone') {
					groupKey = 'archivedMilestones'
				}
			} else if (action.entityType === 'task' || action.entityType === 'milestone') {
				const objectWithDeletedAt = {
					...action.payload,
					deleted_at: moment().format("YYYY-MM-DD HH:mm:ss")
				}

				const archivedKey = action.entityType == 'task' ? 'archivedTasks' : 'archivedMilestones'
				const schemaKey = action.entityType == 'task' ? 'milestoneSchema' : 'projectSchema'

				// I cannot figure out how this causes a memory leak. We clone it above?!
				const _parent = copy(parent)

				if (_parent[archivedKey]) {
					_parent[archivedKey].push(objectWithDeletedAt)
				} else {
					_parent[archivedKey] = [objectWithDeletedAt]
				}

				normalizedResponseData = normalize(_parent, schemas[schemaKey])

				mergeWith(newState, currentEntityState, normalizedResponseData.entities, mergeLogic)
			}

			if (newState) {
				// if task/milestone (newState means we've done a mergeWith)
				state = dotProp.set(state, `entities`, newState)
			} else {
				remove(parent[groupKey], entityId => {
					return entityId === action.payload.id
				})

				currentEntityState = dotProp.set(currentEntityState, `${parentType + 's'}.${parent.id}`, parent)

				const keysToAlwaysRemove = [
					'guestIds',
					'adminIds',
					'projectIds',
					'userIds',
					'pmIds',
					'teamIds',
					'tagIds',
					'invitationIds',
					'commentIds'
				]

				// Remove anything we use pluck() to obtain.
				keysToAlwaysRemove.forEach(keyToAlwaysRemove => {
					if (parent[keyToAlwaysRemove]) {
						const indexIfExists = parent[keyToAlwaysRemove].findIndex(objectId => {
							return objectId == action.payload.id
						})

						if (indexIfExists !== -1) {
							parent[keyToAlwaysRemove].splice(indexIfExists, 1)
						}
					}
				})

				state = dotProp.set(state, `entities`, currentEntityState)
			}

			return state;
		case 'ADDED_GUEST':
			let account = copy(state.entities.accounts[state.currentAccountId])

			if (account.guestIds.indexOf(action.payload.id) === -1) {
				account.guestIds.push(action.payload.id)
			}

			state = dotProp.set(state, `entities.accounts.${account.id}`, account)
			break
		case 'online_users_changed':
			let onlineUsers = [...state.onlineUsers]
			typingUserData = { ...state.typingUserData }

			switch (action.event) {
				case 'init':
					if (action.isFirst) {
						onlineUsers = action.payload
					} else {
						action.payload.forEach(user => {
							if (!onlineUsers.find(onlineUser => {
								return onlineUser.id === user.id
							})) {
								onlineUsers.push(user)
							}
						})
					}

					break
				case 'join':
					onlineUsers.push(action.payload)
					break
				case 'leave':
					const userIndex = onlineUsers.findIndex(user => {
						return user.id === action.payload.id;
					})

					onlineUsers.splice(userIndex, 1)

					typingUserData[action.payload.id] = {
						isTyping: false,
						chatId: action.payload.chatId
					}
					break
				default:
					break
			}

			return {
				...state,
				onlineUsers,
				typingUserData
			}
		case UPDATE_FOLDER_CHAIN:
			return {
				...state,
				folderChain: action.folderChain
			}
		case FOLDER_ORDER_UPDATED:
			currentEntityState = {
				...state.entities
			}

			currentEntityState.folders = {
				...state.entities.folders,
				...copy(action.payload.folders)
			}

			currentEntityState.projects = {
				...state.entities.projects,
				...copy(action.payload.projects)
			}

			return {
				...state,
				entities: currentEntityState
			}
		case GOT_ACCOUNT_DATA:
			if (!action.optimistic) {
				const normalizedAccountData = normalize(action.payload, schemas.accountSchema)

				mergeWith(newState, state.entities, normalizedAccountData.entities, mergeLogic)

				const _response = {
					...state,
					entities: newState,
				}

				if (!action.isSecondaryAccount) {
					_response.currentAccount = action.payload
					_response.currentAccountId = action.payload.id
					_response.accountData = normalizedAccountData.entities.accounts[normalizedAccountData.result]
				} else {
					_response.tempCreatedAccount = action.payload
				}

				return _response
			} else {
				const accountSlug = action.payload
				let optimisticAccount = null

				forIn(state.entities.accounts, account => {
					if (account.slug == accountSlug) {
						optimisticAccount = account
					}
				})

				if (!optimisticAccount) {
					return state
				}

				optimisticAccount.optimistic = true

				return {
					...state,
					currentAccount: optimisticAccount,
					currentAccountId: optimisticAccount.id
				}
				// optimistic. 
			}
		case CLEARED_ACCOUNT_DATA:
			return {
				...state,
				folderChain: null
			}
		case PROJECT_SET_SLUG:
			return {
				...state,
				currentProjectSlug: action.payload
			}
		case PROJECT_STATUSES_SAVED:
			const projectColumns = copy(state.entities.projects[action.payload.projectId].columns)

			projectColumns.forEach((column, index) => {
				if (column.id == action.payload.column.id) {
					projectColumns[index].data = action.payload.updatedStatusData
				}
			})

			state = dotProp.set(state, `entities.projects.${action.payload.projectId}.columns`, projectColumns)

			return state
		case PROJECT_GOT_DATA:
			tempNewState = {
				...state
			}

			if (action.optimistic) {
				reportFillPath('PROJECT_GOT_DATA branch: optimistic', {
					payloadSlug: action.payload
				})

				let projectInState = find(state.entities.projects, project => {
					return project.slug === action.payload
				})

				if (projectInState) {
					tempNewState.currentProjectId = projectInState.id
				}
			} else if (state.currentProjectId == action.payload.id || state.currentProjectSlug == action.payload.slug) {
				// sigh. we need to normalize activity separately otherwise it overwrites because it's stupid.
				const projectActivities = action.payload.activity_items ? {
					...action.payload.activity_items
				} : null

				if (projectActivities) {
					delete action.payload.activity_items
				}

				normalizedResponseData = normalize(action.payload, schemas.projectSchema)

				// Snapshot per-milestone task counts before and after the overwrite
				// merge so we can see whether the full lists are actually written.
				const incomingMilestones = normalizedResponseData.entities.milestones || {}
				const fillBefore = {}
				const fillIncoming = {}

				for (const id in incomingMilestones) {
					const existing = state.entities.milestones && state.entities.milestones[id]
					fillBefore[id] = (existing && Array.isArray(existing.tasks)) ? existing.tasks.length : 'none'
					fillIncoming[id] = Array.isArray(incomingMilestones[id].tasks) ? incomingMilestones[id].tasks.length : 'none'
				}

				mergeWith(newState, state.entities, normalizedResponseData.entities, overwriteFilesAndTasksLogic)

				if (projectActivities) {
					const normalizedActivityData = normalize(projectActivities, [schemas.activitySchema])
					mergeWith(newState, normalizedActivityData.entities, mergeLogic)
				}

				const fillAfter = {}

				for (const id in incomingMilestones) {
					const merged = newState.milestones && newState.milestones[id]
					fillAfter[id] = (merged && Array.isArray(merged.tasks)) ? merged.tasks.length : 'none'
				}

				reportFillPath('PROJECT_GOT_DATA branch: merge', {
					payloadId: action.payload.id,
					payloadSlug: action.payload.slug,
					before: fillBefore,
					incoming: fillIncoming,
					after: fillAfter
				})

				tempNewState.currentProjectId = normalizedResponseData.result
				tempNewState.entities = newState
			} else {
				// The fetched project doesn't match the current slug/id, so the
				// whole response is being thrown away. This is one of the silent
				// paths that can leave a project rendering with no tasks.
				reportSilentPath('PROJECT_GOT_DATA discarded', {
					currentProjectId: state.currentProjectId,
					currentProjectSlug: state.currentProjectSlug,
					payloadId: action.payload && action.payload.id,
					payloadSlug: action.payload && action.payload.slug
				})
			}

			return tempNewState
		case PROJECT_UPDATED:
			return {
				...state,
				currentProjectId: action.payload
			}
		case TASK_SET_CURRENT:
			return {
				...state,
				currentTaskId: action.payload,
			}
		case TASK_LOADING_DATA:
			return { ...state, currentTask: { ...state.currentTask, loading: true } }
		case COMMENT_LIKED:
			if (action.payload.commentId && state.entities.comments[action.payload.commentId]) {
				const clonedComment = copy(state.entities.comments[action.payload.commentId])

				if (clonedComment.likersIds.indexOf(action.payload.userId) !== -1) {
					clonedComment.likersIds.splice(clonedComment.likersIds.indexOf(action.payload.userId), 1)
				} else {
					clonedComment.likersIds.push(action.payload.userId);
				}

				return {
					...state,
					entities: {
						...state.entities,
						comments: {
							...state.entities.comments,
							[clonedComment.id]: clonedComment
						}
					}
				}
			}
		case INVITES_POSTED:
			schema = schemas.accountSchema;

			normalizedResponseData = normalize(action.payload, schema)
			currentEntityState = {}

			merge(currentEntityState, state.entities, normalizedResponseData.entities)

			return {
				...state,
				entities: currentEntityState,
				accountData: currentEntityState.accounts[normalizedResponseData.result]
			}
		case FILE_VERSION_DELETED: {
			const file = {
				...state.entities.files[action.payload.file.id]
			}

			file.versions = file.versions.filter(version => version !== action.payload.versionId);

			const _version = {
				...state.entities.versions[action.payload.versionId],
				deleted_at: 1
			}

			return {
				...state, entities: {
					...state.entities,
					files: {
						...state.entities.files,
						[file.id]: file
					},
					versions: {
						...state.entities.versions,
						[_version.id]: _version
					}
				}
			}
		}
		case 'FILE_PARENT_UPDATED':
			const fileId = action.payload.file.id
			const newTaskId = action.payload.task_id

			return {
				...state,
				entities: {
					...state.entities,
					files: {
						...state.entities.files,
						[fileId]: {
							...state.entities.files[fileId],
							task_id: newTaskId
						}
					}
				}
			}
		case SUBSCRIBER_TOGGLED: {
			let project = copy(action.payload.project)

			let indexIfExists = project.subscriberIds.indexOf(action.payload.user.id)

			if (indexIfExists != -1) {
				project.subscriberIds.splice(indexIfExists, 1)
			} else {
				project.subscriberIds.push(action.payload.user.id)
			}

			return {
				...state,
				entities: {
					...state.entities,
					projects: {
						...state.entities.projects,
						[project.id]: project
					}
				}
			}
		}
		case TYPING_SET:
			typingUserData = copy(state.typingUserData)

			typingUserData[action.payload.userId] = {
				isTyping: action.payload.isTyping,
				chatId: action.payload.chatId
			}

			return {
				...state,
				typingUserData
			}
		case CHAT_ADDED_USER:
			chat = copy(state.entities.chats[action.chat.id])

			if (!chat) {
				return state
			}

			const chatUserIds = chat.userIds ? chat.userIds : chat.users

			const chatUserIndex = chatUserIds.indexOf(action.user.id)

			if (chatUserIndex === -1) {
				chatUserIds.push(action.user.id)
			} else {
				chatUserIds.splice(chatUserIndex, 1)
			}

			chat.userIds = chatUserIds
			chat.users = chatUserIds

			return {
				...state,
				entities: {
					...state.entities,
					chats: {
						...state.entities.chats,
						[chat.id]: chat
					}
				}

			}
		case CHAT_RENAMED:
			let chatObject = copy(state.entities.chats[action.chat.id])

			chatObject.name = action.name;

			return {
				...state,
				entities: {
					...state.entities,
					chats: {
						...state.entities.chats,
						[chatObject.id]: chatObject
					}
				}
			}
		case 'CHAT_SEARCH_RESULTS':
			normalizedResponseData = normalize(action.payload.results, [schemas.chatSearchResultSchema])

			const searchedChat = copy(state.entities.chats[action.payload.chat_id])

			const searchResultsKeys = normalizedResponseData.result.length ? Object.keys(normalizedResponseData.entities.chat_search_results).map(id => parseInt(id)) : []

			searchedChat.search_results = searchResultsKeys

			currentEntityState = {}

			merge(currentEntityState, state.entities, normalizedResponseData.entities)

			currentEntityState.chats[action.payload.chat_id] = searchedChat

			return {
				...state,
				entities: currentEntityState
			}
		case 'MESSAGES_CLEARED':
			const clippedChatItems = action.payload.chat_items.slice(Math.max(action.payload.chat_items.length - 40, 0))

			return {
				...state,
				entities: {
					...state.entities,
					chats: {
						...state.entities.chats,
						[action.payload.id]: {
							...action.payload,
							chat_items: clippedChatItems
						}
					}
				}
			}
		case 'MESSAGE_LIKED':
			if (action.payload.messageId && state.entities.messages[action.payload.messageId]) {
				const clonedMessage = copy(state.entities.messages[action.payload.messageId])

				if (clonedMessage.likersIds.indexOf(action.payload.userId) !== -1) {
					clonedMessage.likersIds.splice(clonedMessage.likersIds.indexOf(action.payload.userId), 1)
				} else {
					clonedMessage.likersIds.push(action.payload.userId)
				}

				return {
					...state,
					entities: {
						...state.entities,
						messages: {
							...state.entities.messages,
							[clonedMessage.id]: clonedMessage
						}
					}
				}
			}
		case NOTIFICATIONS_SEEN:
			if (!state.entities) {
				return state
			}

			forIn(state.entities.notifications, notification => {
				if (!notification.has_seen) {
					const tempNotification = copy(notification)
					tempNotification.has_seen = 1

					applicableNotifications[notification.id] = tempNotification
				}
			})

			mergedNotifications = {}
			merge(mergedNotifications, state.entities.notifications, applicableNotifications)

			return {
				...state,
				entities: {
					...state.entities,
					notifications: mergedNotifications
				}
			}
		case NOTIFICATION_OPENED:
			const notification = copy(state.entities.notifications[action.payload.id])
			notification.has_opened = 1

			return {
				...state,
				entities: {
					...state.entities,
					notifications: {
						...state.entities.notifications,
						[action.payload.id]: notification
					}
				}
			}
		case NOTIFICATIONS_ALL_OPENED:
			forIn(state.entities.notifications, notification => {
				if (!notification.has_opened) {
					const tempNotification = copy(notification)
					tempNotification.has_opened = 1

					applicableNotifications[notification.id] = tempNotification
				}
			})

			mergedNotifications = {}
			merge(mergedNotifications, state.entities.notifications, applicableNotifications)

			return {
				...state,
				entities: {
					...state.entities,
					notifications: mergedNotifications
				}
			}
		case NOTIFICATIONS_CLEARED:
			if (action.notifications.length) {
				newUserData = copy(state.entities.users[state.entities.notifications[action.notifications[0]].recipient_id])

				newUserData.notifications = [];

				return {
					...state,
					entities: {
						...state.entities,
						users: {
							...state.entities.users,
							[newUserData.id]: newUserData
						}
					}
				}
			}
			break
		case 'NOTIFICATIONS_APPENDED': {
			// Normalize the paginated notifications through the schema (rather than
			// spreading raw objects) so their `notifyable` tasks/projects land in
			// entities.tasks / entities.projects — otherwise processNotificationList
			// can't resolve them and the appended notifications never render. The
			// schema also links each notification id onto its recipient, and mergeLogic
			// unions those arrays so nothing is overwritten.
			const normalizedNotifications = normalize(action.payload, [schemas.notificationSchema])

			const appendState = {}
			mergeWith(appendState, { ...state.entities }, normalizedNotifications.entities, mergeLogic)

			const appendedEntities = { ...state.entities }
			forIn(normalizedNotifications.entities, (_, type) => {
				appendedEntities[type] = appendState[type]
			})

			return {
				...state,
				entities: appendedEntities,
			}
		}
		case 'HEADER_TAB_CHANGED':
			return {
				...state,
				activeHeaderTab: action.payload,
			}
		case 'SEE_TASK_NOTIFICATIONS':
			forIn(state.entities.notifications, notification => {
				if (notification.notifyable_type === 'App\\Comment' && !notification.has_opened) {
					const commentObject = state.entities.comments[notification.notifyable_id]

					if (commentObject && commentObject.task_id === action.payload) {
						notification = copy(notification)
						notification.has_opened = 1

						applicableNotifications[notification.id] = notification
					}
				}
			})

			mergedNotifications = {}

			merge(mergedNotifications, state.entities.notifications, applicableNotifications)

			return {
				...state,
				entities: {
					...state.entities,
					notifications: mergedNotifications
				}
			}
		case 'OPTIMISTIC_TODO':
			return {
				...state,
				optimisticTodo: action.payload
			}
		case 'OPTIMISTIC_NEW_CHAT':
			return {
				...state,
				optimisticChat: action.payload
			}
		case 'OPTIMISTIC_CHECKLIST_ITEM':
			return {
				...state,
				optimisticChecklistItems: action.payload ? {
					[action.payload.task_id]: action.payload
				} : null
			}
		case 'KEYBOARD_HEIGHT_UPDATED':
			const os = getMobileOperatingSystem()

			if (os == 'iOS') {
				return {
					...state,
					keyboardHeight: action.payload
				}
			}
		case 'SET_UNSEEN_NOTIFICATIONS':
			let newTitle = document.title.startsWith('(') ? document.title.replace(/ *\([^)]*\) */g, "") : document.title

			if (action.payload) {
				newTitle = '(' + action.payload + ') ' + newTitle
			}

			document.title = newTitle

			return {
				...state,
				unseenNotifications: action.payload
			}
		default:
			break
	}

	return state
}

const handleGeneralMerge = (obj, src, method) => {
	if (isArray(obj)) {
		return union(obj, src)
	} else if (obj && src && obj.updated_at && src.updated_at) {
		if (src.updated_at >= obj.updated_at) {
			return mergeWith(obj, src, method)
		}

		return mergeWith(src, obj, method)
	}
}

function overwriteLogic(objValue, srcValue, key) {
	if (isArray(objValue)) {
		return srcValue
	}
}

function mergeLogic(objValue, srcValue, key) {
	if (shouldAlwaysOverwriteKey(key)) {
		return srcValue
	}
	return handleGeneralMerge(objValue, srcValue, mergeLogic)
}

// Used for PROJECT_GOT_DATA: the project payload is the source of truth for
// its files AND its milestones' task lists, so both are replaced rather than
// unioned. (The getProjectFiles payload has no milestones/tasks keys, so this
// is a no-op for tasks there — mergeWith only visits keys present in the source.)
function overwriteFilesAndTasksLogic(objValue, srcValue, key) {
	if ((key === 'files' || key === 'tasks') && isArray(srcValue) || shouldAlwaysOverwriteKey(key)) {
		return srcValue
	}

	return handleGeneralMerge(objValue, srcValue, overwriteFilesAndTasksLogic)
}

function overwriteTasksLogic(objValue, srcValue, key) {
	if (key === 'tasks' && isArray(srcValue) || shouldAlwaysOverwriteKey(key)) {
		return srcValue;
	}

	return handleGeneralMerge(objValue, srcValue, overwriteTasksLogic)
}

function overwriteUsersLogic(objValue, srcValue, key) {
	if (key === 'users' && isArray(srcValue) || shouldAlwaysOverwriteKey(key)) {
		return srcValue
	}

	return handleGeneralMerge(objValue, srcValue, overwriteUsersLogic)
}

function overwriteGuestsAndInvitesLogic(objValue, srcValue, key) {
	if (key === 'guests' || key === 'invitations' || shouldAlwaysOverwriteKey(key)) {
		return srcValue;
	}

	return handleGeneralMerge(objValue, srcValue, overwriteGuestsAndInvitesLogic)
}

const shouldAlwaysOverwriteKey = key => {
	switch (key) {
		case 'adminIds':
		case 'projectIds':
		case 'userIds':
		case 'pmIds':
		case 'teamIds':
		case 'tagIds':
		case 'guestIds':
		case 'invitationIds':
		case 'collapsed_milestones':
		case 'contact_types':
		case 'liker_ids':
		case 'attendants':
		case 'subscriptions':
		case 'organisation_order':
		case 'favourite_projects':
		case 'onboarded_enums':
		case 'columns':
		case 'tagged_user_ids':
		case 'column_data':
		case 'pulse_data':
		case 'pulse_user_ids':
		case 'applicableUsers':
		case 'upcomingTasks':
		case 'recentlyAddedTasks':
		case 'upcoming':
		case 'recentlyAdded':
		case 'parentColumns':
		case 'teams_included_in_capacity':
		case 'search_results':
			return true
	}

	return false
}